import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import CallIcon from "@material-ui/icons/Call";
import CallEndIcon from "@material-ui/icons/CallEnd";
import MicIcon from "@material-ui/icons/Mic";
import MicOffIcon from "@material-ui/icons/MicOff";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

import ContactAvatar from "../ContactAvatar";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { useCall } from "../../context/Call/CallContext";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  // Acima do conteúdo e dos popups de ticket (Dialog = 1300), abaixo dos toasts.
  root: {
    position: "fixed",
    right: theme.spacing(3),
    bottom: theme.spacing(3),
    zIndex: 1350,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    width: 320,
    maxWidth: `calc(100vw - ${theme.spacing(4)}px)`,
  },
  card: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: 12,
  },
  info: {
    flex: 1,
    minWidth: 0,
  },
  name: {
    fontWeight: 600,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  answerButton: {
    backgroundColor: "#2e7d32",
    color: "#fff",
    "&:hover": { backgroundColor: "#1b5e20" },
  },
  hangUpButton: {
    backgroundColor: "#d32f2f",
    color: "#fff",
    "&:hover": { backgroundColor: "#b71c1c" },
  },
}));

const formatElapsed = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = String(Math.floor(total / 60)).padStart(2, "0");
  const seconds = String(total % 60).padStart(2, "0");
  return `${minutes}:${seconds}`;
};

const formatMoment = (ms) => (ms ? format(new Date(ms), "dd/MM HH:mm") : "");

const callStatusText = (call, now) => {
  if (call.phase === "connected") {
    return i18n.t("calls.status.connected", { time: formatElapsed(now - (call.startedAt || now)) });
  }
  if (call.phase === "ended") {
    const reasonKey = ["rejected", "missed", "mediaFailed"].includes(call.endedReason) ? call.endedReason : "ended";
    const text = i18n.t(`calls.status.endedReasons.${reasonKey}`);
    return call.durationSeconds ? `${text} · ${formatElapsed(call.durationSeconds * 1000)}` : text;
  }
  return i18n.t(`calls.status.${call.phase}`);
};

const PermissionDialog = () => {
  const { permissionPrompt, requestPermission, dialFromPrompt, endStaleCall, closePermissionPrompt } = useCall();
  if (!permissionPrompt) return null;

  const { capabilities = {}, requested, loading } = permissionPrompt;
  const permission = capabilities.permission;
  const contactName = permissionPrompt.ticket?.contact?.name || "";

  let message;
  let primaryAction = null;

  if (capabilities.activeCallId) {
    message = i18n.t("calls.permission.staleCall");
    primaryAction = (
      <ButtonWithSpinner loading={loading} color="primary" variant="contained" onClick={endStaleCall}>
        {i18n.t("calls.permission.endStaleCall")}
      </ButtonWithSpinner>
    );
  } else if (capabilities.callingEnabled === false) {
    message = i18n.t("calls.permission.callingDisabled");
  } else if (!permission) {
    // A consulta falhou: a própria ligação responde se faltar permissão.
    message = i18n.t("calls.permission.lookupFailed");
    primaryAction = (
      <Button color="primary" variant="contained" onClick={dialFromPrompt}>
        {i18n.t("calls.permission.callAnyway")}
      </Button>
    );
  } else if (permission.canStartCall) {
    // Chegou a autorização com o diálogo aberto.
    message = i18n.t("calls.permission.granted", { name: contactName });
    primaryAction = (
      <Button color="primary" variant="contained" startIcon={<CallIcon />} onClick={dialFromPrompt}>
        {i18n.t("calls.permission.callNow")}
      </Button>
    );
  } else if (permission.status !== "no_permission") {
    message = i18n.t("calls.permission.callLimit", { date: formatMoment(permission.startCallAvailableAt) });
  } else {
    const parts = [i18n.t("calls.permission.noPermission", { name: contactName })];
    let canRequest = true;

    if (requested) {
      parts.push(i18n.t("calls.permission.waitingReply"));
    } else if (!capabilities.windowOpen) {
      canRequest = false;
      parts.push(i18n.t("calls.permission.windowClosed"));
    } else if (permission.canRequestPermission === false) {
      canRequest = false;
      parts.push(i18n.t("calls.permission.requestLimit", { date: formatMoment(permission.requestAvailableAt) }));
    }

    message = parts.join("\n\n");
    primaryAction = (
      <ButtonWithSpinner
        loading={loading}
        color="primary"
        variant="contained"
        disabled={!canRequest || loading}
        onClick={requestPermission}
      >
        {requested ? i18n.t("calls.permission.requestAgain") : i18n.t("calls.permission.request")}
      </ButtonWithSpinner>
    );
  }

  return (
    <Dialog open onClose={closePermissionPrompt} maxWidth="xs" fullWidth>
      <DialogTitle>{i18n.t("calls.permission.title")}</DialogTitle>
      <DialogContent>
        <DialogContentText style={{ whiteSpace: "pre-line" }}>{message}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={closePermissionPrompt}>{i18n.t("calls.actions.close")}</Button>
        {primaryAction}
      </DialogActions>
    </Dialog>
  );
};

const CallPanel = () => {
  const classes = useStyles();
  const history = useHistory();
  const { call, incomingCalls, answerIncoming, ignoreIncoming, hangUp, toggleMute } = useCall();
  const [now, setNow] = useState(Date.now());

  const isConnected = call?.phase === "connected";
  useEffect(() => {
    if (!isConnected) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [isConnected]);

  const busy = Boolean(call && call.phase !== "ended");

  const openTicket = (uuid) => {
    if (uuid) history.push(`/tickets/${uuid}`);
  };

  return (
    <>
      <PermissionDialog />
      {(call || incomingCalls.length > 0) && (
        <div className={classes.root}>
          {incomingCalls.map((incoming) => (
            <Paper key={incoming.callId} elevation={8} className={classes.card}>
              <ContactAvatar contact={incoming.contact} />
              <div className={classes.info}>
                <Typography variant="body2" className={classes.name}>
                  {incoming.contact?.name || incoming.contact?.number}
                </Typography>
                <Typography variant="caption" color="textSecondary" component="div">
                  {i18n.t("calls.status.incoming")}
                  {incoming.whatsapp?.name ? ` · ${incoming.whatsapp.name}` : ""}
                </Typography>
              </div>
              <div className={classes.actions}>
                <Tooltip title={busy ? i18n.t("calls.errors.alreadyInCall") : i18n.t("calls.actions.answer")}>
                  <span>
                    <IconButton
                      size="small"
                      className={classes.answerButton}
                      disabled={busy}
                      onClick={() => answerIncoming(incoming)}
                    >
                      <CallIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Button size="small" onClick={() => ignoreIncoming(incoming.callId)}>
                  {i18n.t("calls.actions.ignore")}
                </Button>
              </div>
            </Paper>
          ))}

          {call && (
            <Paper elevation={8} className={classes.card}>
              <ContactAvatar contact={call.contact} />
              <div className={classes.info}>
                <Typography variant="body2" className={classes.name}>
                  {call.contact?.name || call.contact?.number}
                </Typography>
                <Typography variant="caption" color="textSecondary" component="div">
                  {callStatusText(call, now)}
                </Typography>
              </div>
              <div className={classes.actions}>
                {call.ticketUuid && (
                  <Tooltip title={i18n.t("calls.actions.openTicket")}>
                    <IconButton size="small" onClick={() => openTicket(call.ticketUuid)}>
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {busy && (
                  <>
                    <Tooltip title={call.muted ? i18n.t("calls.actions.unmute") : i18n.t("calls.actions.mute")}>
                      <IconButton size="small" onClick={toggleMute}>
                        {call.muted ? <MicOffIcon fontSize="small" color="error" /> : <MicIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={i18n.t("calls.actions.hangUp")}>
                      <IconButton size="small" className={classes.hangUpButton} onClick={() => hangUp()}>
                        <CallEndIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </div>
            </Paper>
          )}
        </div>
      )}
    </>
  );
};

export default CallPanel;
